import React from 'react'
import { Button, Dialog, Field, IconButton, Input, Select, Switch, Tabs } from '@/components/ds'
import { Ic } from '@/components/icons'
import {
  Box,
  BoxHeader,
  BoxLabel,
  BoxRow,
  Pill,
  ProgressBar,
  QueryState,
  RowLabel,
} from '@/components/common'
import {
  archiveCampaign,
  bindCampaignTemplate,
  createCampaign,
  hardDeleteCampaign,
  listCampaignStats,
  listCampaigns,
  listDraftCampaigns,
  listIdentities,
  listTemplates,
  restoreCampaign,
  updateCampaign,
  type CampaignDraft,
} from '@/lib/db'
import { useAsync } from '@/lib/useAsync'
import { useToast } from '@/lib/toast'
import { isValidEmail, relativeTime } from '@/lib/format'
import { MIN_DELAY_MINUTES, describeDelay, validateDelay } from '@shared/schedule'
import { LIFECYCLE_LABEL, campaignLifecycle } from '@shared/lifecycle'
import { DelayControl } from '@/components/DelayControl'
import { CampaignWizard } from './CampaignWizard'
import type { Campaign } from '@shared/types'
import type { ScreenProps } from '@/App'

/**
 * One-shot sessionStorage flag. The Dashboard's "Start new campaign" link sets
 * it before navigating here; this screen opens the new-campaign form on
 * arrival and clears it. Nothing else about this screen changes.
 */
export const OPEN_NEW_CAMPAIGN = 'lm-open-new-campaign'

/** One-shot: set to a campaign id before navigating here to open its form. */
export const OPEN_CAMPAIGN = 'lm-open-campaign'

export function CampaignsScreen({ narrow, onDataChange, onNavigate }: ScreenProps) {
  const toast = useToast()
  // EDIT ONLY. Creating a campaign goes through the wizard, and the type here
  // has no 'new' member so a creation path can't be reintroduced by accident.
  const [editing, setEditing] = React.useState<Campaign | null>(null)
  /**
   * One confirmation dialog, three different destructions:
   *   archive  a real campaign leaves the list, keeping every row  (reversible)
   *   discard  an empty wizard draft, which has no history to lose (real delete)
   *   purge    "Delete permanently" from the archive — the cascade (irreversible)
   */
  const [confirm, setConfirm] = React.useState<{
    kind: 'archive' | 'discard' | 'purge'
    campaign: Campaign
  } | null>(null)
  const [tab, setTab] = React.useState('active')
  const [busy, setBusy] = React.useState(false)

  const [wizard, setWizard] = React.useState<{ open: boolean; resumeId: string | null }>({
    open: false,
    resumeId: null,
  })
  /** The minimal "record only" form — no leads, no template, saved as a draft. */
  const [quickCreate, setQuickCreate] = React.useState(false)

  // LIVE VIEW: while any campaign is active/scheduled, the list and its stats
  // silently refresh every 8s (visible tab only, stale data kept between
  // polls) — so Status flips to Done and Progress/Today move during a drain.
  const [liveWork, setLiveWork] = React.useState(false)
  const campaigns = useAsync(() => listCampaigns(), [], {
    refreshEvery: 8000,
    refreshActive: liveWork,
  })
  const drafts = useAsync(() => listDraftCampaigns(), [])
  const stats = useAsync(() => listCampaignStats(), [], {
    refreshEvery: 8000,
    refreshActive: liveWork,
  })
  // The archive: put away, not gone. Nothing here can be sending, so it never
  // polls — it changes only when the user archives or restores something.
  const archived = useAsync(() => listCampaigns({ archived: true }), [])
  const archivedStats = useAsync(() => listCampaignStats({ archived: true }), [])
  const identities = useAsync(() => listIdentities(), [])

  React.useEffect(() => {
    setLiveWork(
      (campaigns.data ?? []).some((c) => c.status === 'active' || c.status === 'scheduled'),
    )
  }, [campaigns.data])

  // Arriving via the Dashboard's "Start new campaign" link opens the wizard.
  React.useEffect(() => {
    if (sessionStorage.getItem(OPEN_NEW_CAMPAIGN)) {
      sessionStorage.removeItem(OPEN_NEW_CAMPAIGN)
      setWizard({ open: true, resumeId: null })
    }
  }, [])

  // Arriving via a Dashboard campaign row opens that campaign's form.
  React.useEffect(() => {
    const id = sessionStorage.getItem(OPEN_CAMPAIGN)
    if (id && campaigns.data) {
      sessionStorage.removeItem(OPEN_CAMPAIGN)
      const c = campaigns.data.find((x) => x.id === id)
      if (c) setEditing(c)
    }
  }, [campaigns.data])

  const statsById = new Map((stats.data ?? []).map((s) => [s.campaign_id, s]))
  const rows = campaigns.data ?? []
  const archivedRows = archived.data ?? []
  const archivedStatsById = new Map((archivedStats.data ?? []).map((s) => [s.campaign_id, s]))

  function refresh() {
    campaigns.reload()
    stats.reload()
    archived.reload()
    archivedStats.reload()
    onDataChange()
  }

  async function togglePause(c: Campaign) {
    setBusy(true)
    try {
      const next = c.status === 'active' ? 'paused' : 'active'
      await updateCampaign(c.id, { status: next })
      toast('info', `${c.name} ${next === 'paused' ? 'paused' : 'resumed'}`)
      refresh()
    } catch (e) {
      toast('error', 'Update failed', e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  /** Archive / discard-draft / delete-permanently, all from the one dialog. */
  async function confirmAction() {
    if (!confirm) return
    const { kind, campaign: c } = confirm
    setBusy(true)
    try {
      if (kind === 'archive') {
        await archiveCampaign(c.id)
        toast('info', `${c.name} archived`, 'Its stats are kept — restore it anytime')
      } else if (kind === 'discard') {
        await hardDeleteCampaign(c.id)
        toast('info', `${c.name || 'Draft'} discarded`)
        drafts.reload()
      } else {
        await hardDeleteCampaign(c.id)
        toast('info', `${c.name} deleted permanently`, 'Its leads, queue rows and stats are gone')
      }
      setConfirm(null)
      refresh()
    } catch (e) {
      const verb = kind === 'archive' ? 'Archive' : 'Delete'
      toast('error', `${verb} failed`, e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  async function restore(c: Campaign) {
    setBusy(true)
    try {
      await restoreCampaign(c.id)
      toast('success', `${c.name} restored`, 'Back in the list, exactly as it was')
      refresh()
    } catch (e) {
      toast('error', 'Restore failed', e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  // Shared grid template so the header strip and the rows align exactly.
  const TMPL = 'minmax(200px,1.6fr) 84px 200px 92px 60px 104px'
  // The archive has no live work to show — no progress, no daily cap.
  const ARCHIVE_TMPL = 'minmax(200px,1.6fr) 140px 80px 70px 150px'

  // The wizard owns the whole content area while open.
  if (wizard.open) {
    return (
      <CampaignWizard
        resumeId={wizard.resumeId}
        narrow={narrow}
        onClose={() => {
          setWizard({ open: false, resumeId: null })
          drafts.reload()
          refresh()
        }}
        onFinished={(c, outcome) => {
          setWizard({ open: false, resumeId: null })
          drafts.reload()
          refresh()
          toast(
            'success',
            `${c.name} ${outcome === 'scheduled' ? 'scheduled' : 'is live'}`,
            outcome === 'scheduled'
              ? 'The first send waits for the start time'
              : 'Leads schedule with the campaign delay',
          )
          onNavigate('queue')
        }}
      />
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="wrap-sm" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Tabs
          tabs={[
            { value: 'active', label: 'Campaigns' },
            {
              value: 'archived',
              label: `Archived${archivedRows.length ? ` (${archivedRows.length})` : ''}`,
            },
          ]}
          value={tab}
          onChange={setTab}
        />
        <div style={{ flex: 1 }} />
        <CreateButtons
          onWizard={() => setWizard({ open: true, resumeId: null })}
          onQuick={() => setQuickCreate(true)}
        />
      </div>

      {/* unfinished wizard drafts */}
      {tab === 'active' && (drafts.data ?? []).length > 0 && (
        <div
          style={{
            border: '1px solid var(--border-default)',
            borderRadius: 6,
            overflow: 'hidden',
            background: 'var(--surface-box)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '7px 12px',
              background: 'var(--surface-box-header)',
              borderBottom: '1px solid var(--border-default)',
              fontSize: 11.5,
              fontWeight: 700,
              color: 'var(--text-faint)',
              textTransform: 'uppercase',
              letterSpacing: '.05em',
            }}
          >
            Unfinished drafts
          </div>
          {(drafts.data ?? []).map((d, i, arr) => (
            <div
              key={d.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                borderBottom: i === arr.length - 1 ? 'none' : '1px solid var(--border-row)',
                fontSize: 13,
              }}
            >
              <span style={{ fontWeight: 600 }}>{d.name || '(unnamed draft)'}</span>
              <Pill bg="var(--pill-neutral-bg)" fg="var(--text-faint)">
                draft
              </Pill>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                started {relativeTime(d.created_at)}
              </span>
              <div style={{ flex: 1 }} />
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setWizard({ open: true, resumeId: d.id })}
              >
                Resume
              </Button>
              <IconButton
                label="Discard draft"
                size="sm"
                onClick={() => setConfirm({ kind: 'discard', campaign: d })}
              >
                <Ic.trash size={15} />
              </IconButton>
            </div>
          ))}
        </div>
      )}

      {tab === 'active' && (
      <QueryState
        loading={campaigns.loading}
        error={campaigns.error}
        empty={rows.length === 0}
        emptyTitle="No campaigns yet"
        emptyDescription="A campaign holds its own template, leads, delay settings, and pacing."
        emptyAction={
          <CreateButtons
            onWizard={() => setWizard({ open: true, resumeId: null })}
            onQuick={() => setQuickCreate(true)}
          />
        }
        onRetry={refresh}
      >
        <Box dense>
          <div className="scroll-x">
            <div style={{ minWidth: 800 }}>
              <BoxHeader style={{ display: 'grid', gridTemplateColumns: TMPL, gap: 10 }}>
                <BoxLabel>Campaign</BoxLabel>
                <BoxLabel>Status</BoxLabel>
                <BoxLabel>Progress</BoxLabel>
                <BoxLabel style={{ justifySelf: 'end' }}>Today</BoxLabel>
                <BoxLabel style={{ justifySelf: 'end' }}>Failed</BoxLabel>
                <span />
              </BoxHeader>

              {rows.map((c, i) => {
                const s = statsById.get(c.id)
                const failed = Number(s?.failed ?? 0)
                return (
                  <BoxRow
                    key={c.id}
                    last={i === rows.length - 1}
                    style={{ display: 'grid', gridTemplateColumns: TMPL, gap: 10 }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 600,
                          color: 'var(--text-primary)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {c.name}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: 'var(--text-muted)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {describeDelay(c)}
                      </div>
                    </div>

                    {/* Derived from remaining work, NOT from campaigns.status —
                        the same call the sidebar makes, so the badge and the
                        "N campaigns live" count are the same number. */}
                    <span>
                      {(() => {
                        const phase = campaignLifecycle(c, s)
                        const tone =
                          phase === 'live'
                            ? { bg: 'var(--status-approved-bg)', fg: 'var(--status-approved-fg)' }
                            : phase === 'done'
                              ? { bg: 'var(--status-sent-bg)', fg: 'var(--status-sent-fg)' }
                              : { bg: 'var(--pill-neutral-bg)', fg: 'var(--text-faint)' }
                        return (
                          <Pill bg={tone.bg} fg={tone.fg}>
                            {LIFECYCLE_LABEL[phase]}
                          </Pill>
                        )
                      })()}
                    </span>

                    {/* fixed-width bar + count, never stretched across the column */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 120, flex: 'none' }}>
                        <ProgressBar value={Number(s?.sent ?? 0)} max={Number(s?.total ?? 0)} warnAt={2} />
                      </div>
                      <span className="tnum" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {Number(s?.sent ?? 0)}/{Number(s?.total ?? 0)}
                      </span>
                    </div>

                    <span
                      className="tnum"
                      style={{ justifySelf: 'end', fontSize: 12.5, color: 'var(--text-secondary)' }}
                    >
                      {Number(s?.sent_today ?? 0)}/{c.daily_cap}
                    </span>

                    <span
                      className="tnum"
                      style={{
                        justifySelf: 'end',
                        fontSize: 12.5,
                        color: failed ? 'var(--status-failed-fg)' : 'var(--text-faint)',
                      }}
                    >
                      {failed || '—'}
                    </span>

                    <span style={{ display: 'inline-flex', gap: 2, justifySelf: 'end' }}>
                      <IconButton
                        label={c.status === 'active' ? 'Pause' : 'Resume'}
                        size="sm"
                        disabled={busy}
                        onClick={() => togglePause(c)}
                      >
                        {c.status === 'active' ? <Ic.pause size={15} /> : <Ic.play size={15} />}
                      </IconButton>
                      <IconButton label="Edit" size="sm" onClick={() => setEditing(c)}>
                        <Ic.pencil size={15} />
                      </IconButton>
                      {/* ARCHIVE, not delete. The cascade off `campaigns` takes
                          the send_queue rows the whole dashboard counts — see
                          0021_campaign_archive.sql. */}
                      <IconButton
                        label="Archive"
                        size="sm"
                        onClick={() => setConfirm({ kind: 'archive', campaign: c })}
                      >
                        <Ic.trash size={15} />
                      </IconButton>
                    </span>
                  </BoxRow>
                )
              })}
            </div>
          </div>
        </Box>
      </QueryState>
      )}

      {tab === 'archived' && (
        <QueryState
          loading={archived.loading}
          error={archived.error}
          empty={archivedRows.length === 0}
          emptyTitle="Nothing archived"
          emptyDescription="Archiving a campaign takes it off the list but keeps every lead, queue row and statistic it earned — so the Dashboard and Analytics never lose its history."
          onRetry={refresh}
        >
          <Box dense>
            <div className="scroll-x">
              <div style={{ minWidth: 800 }}>
                <BoxHeader style={{ display: 'grid', gridTemplateColumns: ARCHIVE_TMPL, gap: 10 }}>
                  <BoxLabel>Campaign</BoxLabel>
                  <BoxLabel>Archived</BoxLabel>
                  <BoxLabel style={{ justifySelf: 'end' }}>Sent</BoxLabel>
                  <BoxLabel style={{ justifySelf: 'end' }}>Failed</BoxLabel>
                  <span />
                </BoxHeader>

                {archivedRows.map((c, i) => {
                  const s = archivedStatsById.get(c.id)
                  const failed = Number(s?.failed ?? 0)
                  return (
                    <BoxRow
                      key={c.id}
                      last={i === archivedRows.length - 1}
                      style={{ display: 'grid', gridTemplateColumns: ARCHIVE_TMPL, gap: 10 }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {c.name}
                        </div>
                        {/* The status it was in when it was put away — archiving
                            never rewrites it, so Restore is a true undo. */}
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          was {c.status}
                        </div>
                      </div>

                      <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                        {c.archived_at ? relativeTime(c.archived_at) : '—'}
                      </span>

                      <span
                        className="tnum"
                        style={{ justifySelf: 'end', fontSize: 12.5, color: 'var(--text-secondary)' }}
                      >
                        {Number(s?.sent ?? 0)}
                      </span>

                      <span
                        className="tnum"
                        style={{
                          justifySelf: 'end',
                          fontSize: 12.5,
                          color: failed ? 'var(--status-failed-fg)' : 'var(--text-faint)',
                        }}
                      >
                        {failed || '—'}
                      </span>

                      <span style={{ display: 'inline-flex', gap: 6, justifySelf: 'end' }}>
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={busy}
                          onClick={() => restore(c)}
                        >
                          Restore
                        </Button>
                        <IconButton
                          label="Delete permanently"
                          size="sm"
                          onClick={() => setConfirm({ kind: 'purge', campaign: c })}
                        >
                          <Ic.trash size={15} />
                        </IconButton>
                      </span>
                    </BoxRow>
                  )
                })}
              </div>
            </div>
          </Box>
        </QueryState>
      )}

      {quickCreate && (
        <QuickCampaignDialog
          identities={(identities.data ?? []).map((i) => ({
            value: i.id,
            label: `${i.label} — ${i.from_email}`,
          }))}
          onClose={() => setQuickCreate(false)}
          onCreated={(c) => {
            setQuickCreate(false)
            toast(
              'success',
              `${c.name} created`,
              'Saved as a draft — add a template and leads before it can send',
            )
            drafts.reload()
            refresh()
            // Straight into its form, where the template picker lives.
            setEditing(c)
          }}
        />
      )}

      {editing && (
        <CampaignDialog
          campaign={editing}
          identities={(identities.data ?? []).map((i) => ({
            value: i.id,
            label: `${i.label} — ${i.from_email}`,
          }))}
          onClose={() => setEditing(null)}
          onSaved={(name) => {
            setEditing(null)
            toast('success', `${name} saved`)
            refresh()
          }}
        />
      )}

      <Dialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={
          confirm?.kind === 'archive'
            ? `Archive ${confirm.campaign.name}?`
            : confirm?.kind === 'discard'
              ? `Discard ${confirm.campaign.name || 'this draft'}?`
              : `Permanently delete ${confirm?.campaign.name}?`
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button
              variant={confirm?.kind === 'archive' ? 'primary' : 'danger'}
              disabled={busy}
              onClick={confirmAction}
            >
              {confirm?.kind === 'archive'
                ? 'Archive'
                : confirm?.kind === 'discard'
                  ? 'Discard'
                  : 'Delete permanently'}
            </Button>
          </>
        }
      >
        {confirm?.kind === 'archive' ? (
          <>
            It leaves the Campaigns list and every campaign picker, and stops sending immediately —
            anything still queued stays put but never goes out. Its leads, queue rows and delivery
            history are all kept, so the Dashboard and Analytics keep counting it. You can restore it
            at any time from the Archived tab.
          </>
        ) : confirm?.kind === 'discard' ? (
          <>This draft was never launched, so there is nothing to keep.</>
        ) : (
          <>
            <strong>This cannot be undone, and its statistics go with it.</strong> Deleting removes
            the leads and every queue row including the ones already sent — so this campaign's sends,
            opens and clicks disappear from the Dashboard and Analytics for good. Restore it instead
            if you only want it off the list.
          </>
        )}
      </Dialog>
    </div>
  )
}

// -----------------------------------------------------------------------------

/**
 * EDIT-ONLY settings dialog for a campaign that already exists.
 *
 * Creating a campaign is the wizard's job — `campaign` is required here, so
 * this component cannot be opened for creation at all.
 */
function CampaignDialog({
  campaign,
  identities,
  onClose,
  onSaved,
}: {
  campaign: Campaign
  identities: { value: string; label: string }[]
  onClose: () => void
  onSaved: (name: string) => void
}) {
  const toast = useToast()
  const [draft, setDraft] = React.useState<CampaignDraft>(() => ({
    name: campaign.name,
    from_name: campaign.from_name,
    reply_to: campaign.reply_to,
    company: campaign.company,
    sender_title: campaign.sender_title,
    smtp_account_id: campaign.smtp_account_id,
    delay_enabled: campaign.delay_enabled,
    delay_mode: campaign.delay_mode,
    delay_hours: Number(campaign.delay_hours),
    delay_minutes: campaign.delay_minutes,
    random_window_minutes: campaign.random_window_minutes,
    daily_cap: campaign.daily_cap,
    throttle_seconds: campaign.throttle_seconds,
    status: campaign.status,
    starts_at: campaign.starts_at,
    window_enabled: campaign.window_enabled,
    window_start_min: campaign.window_start_min,
    window_end_min: campaign.window_end_min,
    send_tz: campaign.send_tz,
  }))
  const [busy, setBusy] = React.useState(false)

  const set = <K extends keyof CampaignDraft>(key: K, value: CampaignDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }))

  async function save() {
    if (!draft.name.trim()) {
      toast('error', 'Name it first')
      return
    }
    if (draft.reply_to?.trim() && !isValidEmail(draft.reply_to)) {
      toast('error', 'Reply-to looks wrong', draft.reply_to)
      return
    }
    const delayProblem = validateDelay(draft)
    if (delayProblem) {
      toast('error', 'Delay is too short', delayProblem)
      return
    }
    setBusy(true)
    try {
      await updateCampaign(campaign.id, draft)
      onSaved(draft.name)
    } catch (e) {
      toast('error', 'Save failed', e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      width={560}
      title={`Edit ${campaign.name}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={busy} onClick={save}>
            {busy ? 'Saving…' : 'Save changes'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Field label="Name">
          <Input
            value={draft.name}
            onChange={(v) => set('name', v)}
            placeholder="Q3 launch"
            autoFocus
          />
        </Field>

        <Field
          label="Send from"
          hint={
            identities.length
              ? 'The identity whose SMTP mailbox delivers this campaign'
              : 'No identities yet — add one on the Identities screen first'
          }
        >
          <Select
            options={[{ value: '', label: 'Use the default identity' }, ...identities]}
            value={draft.smtp_account_id ?? ''}
            onChange={(v) => set('smtp_account_id', v || null)}
          />
        </Field>

        {/* --- sender presentation ------------------------------------------- */}
        <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="From name (optional)" hint="Overrides the sender's default display name">
            <Input
              value={draft.from_name ?? ''}
              onChange={(v) => set('from_name', v || null)}
              placeholder="StuHelp"
            />
          </Field>
          <Field
            label="Reply-to email"
            hint="Sent from your connected mailbox; replies go here instead."
          >
            <Input
              type="email"
              value={draft.reply_to ?? ''}
              onChange={(v) => set('reply_to', v || null)}
              placeholder="hiring@company.com"
            />
          </Field>
        </div>

        {/* --- send-wide merge constants -------------------------------------
            {{company}} / {{sender_title}} are the same in every email of this
            campaign. They are NOT CSV columns — a lead named `company` cannot
            override them. */}
        <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Company" hint="The hiring organisation — fills {{company}} everywhere">
            <Input
              value={draft.company ?? ''}
              onChange={(v) => set('company', v || null)}
              placeholder="Millenium Eye Center"
            />
          </Field>
          <Field label="Sender title (optional)" hint="Fills {{sender_title}}, e.g. Talent Team">
            <Input
              value={draft.sender_title ?? ''}
              onChange={(v) => set('sender_title', v || null)}
              placeholder="Talent Team"
            />
          </Field>
        </div>

        {/* --- which template this campaign sends --------------------------- */}
        <CampaignTemplateField campaign={campaign} />

        {/* --- delay: the SAME control the wizard uses ----------------------- */}
        <DelayControl
          value={{
            delay_enabled: draft.delay_enabled,
            delay_mode: draft.delay_mode,
            delay_minutes: draft.delay_minutes,
            delay_hours: draft.delay_hours,
            random_window_minutes: draft.random_window_minutes,
          }}
          onChange={(next) => setDraft((d) => ({ ...d, ...next }))}
        />

        {/* --- rate limits -------------------------------------------------- */}
        <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Daily cap" hint="Max sends per UTC day">
            <Input
              type="number"
              min={1}
              value={String(draft.daily_cap)}
              onChange={(v) => set('daily_cap', Number(v) || 1)}
            />
          </Field>
          <Field label="Throttle" hint="Seconds between sends">
            <Input
              type="number"
              min={0}
              value={String(draft.throttle_seconds)}
              onChange={(v) => set('throttle_seconds', Number(v) || 0)}
            />
          </Field>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            borderTop: '1px solid var(--border-default)',
            paddingTop: 14,
          }}
        >
          <RowLabel
            title="Active"
            hint="Paused campaigns keep their queue but the Worker skips them"
          />
          <Switch
            checked={draft.status === 'active'}
            onChange={(v) => set('status', v ? 'active' : 'paused')}
          />
        </div>
      </div>
    </Dialog>
  )
}


/**
 * The two ways to make a campaign, side by side.
 *
 * The wizard stays primary — it is the path that ends with something that can
 * actually send. "Create campaign only" is the secondary escape hatch for
 * setting up the record now and filling in leads and a template later.
 */
function CreateButtons({ onWizard, onQuick }: { onWizard: () => void; onQuick: () => void }) {
  return (
    <div className="wrap-sm" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <Button size="sm" icon={<Ic.plus size={14} />} onClick={onWizard}>
        New campaign
      </Button>
      <Button
        size="sm"
        variant="secondary"
        onClick={onQuick}
        title="Create just the campaign record — add leads and a template afterwards"
      >
        Create campaign only
      </Button>
    </div>
  )
}

/**
 * The minimal creation form: name, sender, presentation, pacing. No leads step,
 * no template step.
 *
 * It writes status 'draft' deliberately. Draft is not in
 * SENDABLE_CAMPAIGN_STATUSES, so the Worker skips it outright — and the same
 * readiness guard the wizard uses (@shared/readiness, via getCampaignReadiness)
 * still refuses to activate it until a template is bound and at least one lead
 * exists. Nothing here can produce a campaign that sends half-configured.
 */
function QuickCampaignDialog({
  identities,
  onClose,
  onCreated,
}: {
  identities: { value: string; label: string }[]
  onClose: () => void
  onCreated: (campaign: Campaign) => void
}) {
  const toast = useToast()
  const [draft, setDraft] = React.useState<CampaignDraft>({
    name: '',
    smtp_account_id: null,
    company: null,
    sender_title: null,
    // Matches the wizard: a new campaign has no delay and sends on launch.
    delay_enabled: false,
    delay_mode: 'random_1h',
    delay_hours: 1,
    delay_minutes: MIN_DELAY_MINUTES,
    random_window_minutes: 60,
    daily_cap: 500,
    throttle_seconds: 60,
    status: 'draft',
    from_name: null,
    reply_to: null,
    starts_at: null,
    window_enabled: false,
    window_start_min: 540,
    window_end_min: 1020,
    send_tz: null,
  })
  const [busy, setBusy] = React.useState(false)

  const set = <K extends keyof CampaignDraft>(key: K, value: CampaignDraft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }))

  async function save() {
    if (!draft.name.trim()) {
      toast('error', 'Name it first')
      return
    }
    if (draft.reply_to?.trim() && !isValidEmail(draft.reply_to)) {
      toast('error', 'Reply-to looks wrong', draft.reply_to)
      return
    }
    const delayProblem = validateDelay(draft)
    if (delayProblem) {
      toast('error', 'Delay is too short', delayProblem)
      return
    }
    setBusy(true)
    try {
      const created = await createCampaign({ ...draft, name: draft.name.trim(), status: 'draft' })
      onCreated(created)
    } catch (e) {
      toast('error', 'Could not create', e instanceof Error ? e.message : String(e))
      setBusy(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      width={560}
      title="Create campaign only"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={busy} onClick={save}>
            {busy ? 'Creating…' : 'Create campaign'}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div
          style={{
            display: 'flex',
            gap: 9,
            padding: '9px 11px',
            borderRadius: 10,
            background: 'var(--accent-soft)',
            border: '1px solid var(--accent-soft-border)',
            fontSize: 12.5,
            lineHeight: 1.45,
            color: 'var(--accent-text)',
          }}
        >
          <Ic.warning size={15} style={{ flex: 'none', marginTop: 1 }} />
          <span>
            Saved as a <strong>draft</strong>. It cannot send until you bind a template and add at
            least one lead — both from its form afterwards.
          </span>
        </div>

        <Field label="Name">
          <Input
            value={draft.name}
            onChange={(v) => set('name', v)}
            placeholder="Q3 launch"
            autoFocus
          />
        </Field>

        <Field
          label="Send from"
          hint={
            identities.length
              ? 'The identity whose SMTP mailbox delivers this campaign'
              : 'No identities yet — add one on the Identities screen first'
          }
        >
          <Select
            options={[{ value: '', label: 'Use the default identity' }, ...identities]}
            value={draft.smtp_account_id ?? ''}
            onChange={(v) => set('smtp_account_id', v || null)}
          />
        </Field>

        <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="From name (optional)" hint="Overrides the sender's default display name">
            <Input
              value={draft.from_name ?? ''}
              onChange={(v) => set('from_name', v || null)}
              placeholder="StuHelp"
            />
          </Field>
          <Field label="Reply-to email" hint="Replies land here instead of the sending mailbox">
            <Input
              type="email"
              value={draft.reply_to ?? ''}
              onChange={(v) => set('reply_to', v || null)}
              placeholder="hiring@company.com"
            />
          </Field>
        </div>

        <DelayControl
          value={{
            delay_enabled: draft.delay_enabled,
            delay_mode: draft.delay_mode,
            delay_minutes: draft.delay_minutes,
            delay_hours: draft.delay_hours,
            random_window_minutes: draft.random_window_minutes,
          }}
          onChange={(next) => setDraft((d) => ({ ...d, ...next }))}
        />

        <div className="grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Daily cap" hint="Max sends per UTC day">
            <Input
              type="number"
              min={1}
              value={String(draft.daily_cap)}
              onChange={(v) => set('daily_cap', Number(v) || 1)}
            />
          </Field>
          <Field label="Throttle" hint="Seconds between sends">
            <Input
              type="number"
              min={0}
              value={String(draft.throttle_seconds)}
              onChange={(v) => set('throttle_seconds', Number(v) || 0)}
            />
          </Field>
        </div>
      </div>
    </Dialog>
  )
}

/**
 * Bind (or re-bind) the campaign's template.
 *
 * Saved on change rather than with the rest of the form: template_id is not
 * part of CampaignDraft, so a stale draft can never wipe it. Re-binding also
 * re-stamps the rows already waiting in the queue, which is what makes an
 * unbound campaign's backlog sendable again.
 */
function CampaignTemplateField({ campaign }: { campaign: Campaign }) {
  const toast = useToast()
  // The whole library: any saved template can back any campaign.
  const templates = useAsync(() => listTemplates(), [])
  const [boundId, setBoundId] = React.useState(campaign.template_id ?? '')
  const [busy, setBusy] = React.useState(false)

  const rows = templates.data ?? []

  async function bind(templateId: string) {
    if (!templateId || templateId === boundId) return
    setBusy(true)
    try {
      const { template } = await bindCampaignTemplate(campaign.id, templateId)
      setBoundId(template.id)
      toast('success', 'Template bound', 'Emails already queued for this campaign send with it too')
    } catch (e) {
      toast('error', 'Could not bind', e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Field
      label="Template"
      hint={
        rows.length === 0
          ? 'Your template library is empty - create one on the Templates screen.'
          : boundId
            ? 'What every queued email composes from, including the ones already waiting. Editing the template changes every campaign using it.'
            : 'Nothing is bound, so this campaign cannot send. Pick one.'
      }
      error={rows.length > 0 && !boundId ? 'No template bound' : undefined}
    >
      <Select
        options={[
          { value: '', label: rows.length ? 'Select a template...' : 'No templates yet' },
          ...rows.map((t) => ({
            value: t.id,
            label: t.name?.trim() || t.subject || '(no subject)',
          })),
        ]}
        value={boundId}
        error={rows.length > 0 && !boundId}
        disabled={busy || templates.loading || rows.length === 0}
        onChange={bind}
      />
    </Field>
  )
}
